// Zebra Offline Reports — Issue Screen Panel button.
//
// Lists templates applicable to the current issue type, filtered by the
// admin's `issue_type_ids` field. Empty list → hidden (not even a stub).
// Each entry in the dropdown has a Download action and an Email action
// so an end-user can either save the file locally or send it.

import { useState, useEffect, useRef, useCallback } from 'react';
import api, { triggerBlobDownload, errorMessage } from '@modules/reports/frontend/api';
import { outputsFor } from '@modules/reports/frontend/formats';
import '@modules/reports/frontend/components/ExportReportButton.css';

export default function ExportReportButton({ issueKey, issueTypeId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderingId, setRenderingId] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Re-fetch when issueTypeId changes (so opening a different work-item
  // surfaces a different template list).
  useEffect(() => {
    if (!issueKey) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Scope the ask: this surface, this work type, this project. A report
    // the admin limited to other projects never appears here.
    api.listReports({
      issueTypeId: issueTypeId || null,
      projectKey: issueKey ? String(issueKey).replace(/-\d+$/, '') : null,
      surface: 'item',
      enabledOnly: true,
    })
      .then(list => {
        if (!cancelled) setTemplates(Array.isArray(list) ? list : []);
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('[OfflineReports] template list failed:', err.message);
          setTemplates([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [issueKey, issueTypeId]);

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const renderOne = useCallback(async (tpl, fmt) => {
    setRenderingId(tpl.id);
    setError(null);
    setOpen(false);
    try {
      const { blob, filename } = await api.renderReport(tpl.id, { issueKey, outputFormat: fmt });
      triggerBlobDownload(blob, filename);
    } catch (err) {
      setError(await errorMessage(err, 'Render failed'));
      // Auto-clear after 6s so the panel doesn't stay broken-looking.
      setTimeout(() => setError(null), 6000);
    } finally {
      setRenderingId(null);
    }
  }, [issueKey]);

  // Hide entirely while loading the first time, or if no applicable templates.
  if (loading) return null;
  if (templates.length === 0) return null;

  const busy = renderingId !== null;

  return (
    <div className="or-export-wrap" ref={wrapperRef}>
      <button
        className="or-export-btn"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        title="Generate report…"
      >
        {busy ? (
          <svg className="or-export-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="9" y1="13" x2="15" y2="13"/>
            <line x1="9" y1="17" x2="15" y2="17"/>
          </svg>
        )}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="or-export-menu">
          <div className="or-export-menu-title">Generate report</div>
          {templates.map(tpl => (
            <div key={tpl.id} className="or-export-menu-row">
              <div className="or-export-menu-info">
                <span className="or-export-menu-name">{tpl.name}</span>
                {tpl.description && <span className="or-export-menu-desc">{tpl.description}</span>}
              </div>
              <div className="or-export-menu-actions">
                {outputsFor(tpl).map(opt => (
                  <button
                    key={opt.fmt}
                    className="or-export-fmt-btn"
                    onClick={() => renderOne(tpl, opt.fmt)}
                    disabled={busy}
                    title={`Download as ${opt.label}`}
                    aria-label={`Download ${tpl.name} as ${opt.label}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="or-export-error" onClick={() => setError(null)} title="Click to dismiss">
          {error}
        </div>
      )}
    </div>
  );
}
